// FILE: components/pages/ReviewDetailPage.tsx
"use client";

import Link from "next/link";
import { useState, useMemo, useEffect } from "react";
import {
  ArrowLeft, Star, ThumbsUp, MessageSquare, Gift, Share2, MoreVertical, Copy, Flag, Check,
  Bookmark, Eye, CheckCircle2, Pencil, X, Save,
  BookOpen, Users, Clock, ChevronRight, Sparkles, Bot,
  Search, Plus, FileText, Link2, AlertCircle, Target,
  Send, Smile, Copy as CopyIcon,
} from "lucide-react";
import UserLink from "@/components/shared/UserLink";
import UserHoverCard from "@/components/shared/UserHoverCard";
import ReviewDetail from "@/components/reviews/ReviewDetail";
import type { ReviewData } from "@/components/reviews/types";

/* ═══════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════ */

interface AttachedQuestion {
  id: string;
  text: string;
  difficulty: string;
  section: string;
  type: "matched" | "new";
  matchRate?: string;
}

interface RelatedItem {
  id: string;
  title: string;
  author: string;
  emoji: string;
  type: "path" | "exam";
  matchCount: number;
  totalQuestions: number;
  rating: number;
}

const GLOBAL_QUESTION_BANK: { id: string; text: string; difficulty: string; section: string }[] = [];

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */

interface Props {
  onBack?: () => void;
  onNavigate?: (page: string) => void;
  reviewId?: string;
  initialReview?: any; // server-passed review, skip fetch
}

export default function ReviewDetailPage({ onBack, onNavigate, reviewId: reviewIdProp, initialReview }: Props) {
  const reviewId = reviewIdProp ?? initialReview?.id;
  // ── ALL HOOKS FIRST — before any early return ──
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  async function handleCopyUUID() {
    if (!reviewId) return;
    try {
      await navigator.clipboard.writeText(reviewId);
      setCopied(true);
      setTimeout(() => { setCopied(false); setMenuOpen(false); }, 1200);
    } catch {
      setMenuOpen(false);
    }
  }
  const [dbReview, setDbReview] = useState<ReviewData | null>(() => {
    if (!initialReview) return null;
    const uName = initialReview.user?.full_name ?? initialReview.user?.username ?? "Người dùng";
    return {
      id: initialReview.id,
      author: uName,
      authorId: initialReview.user?.id ?? initialReview.user_id ?? "",
      avatar: initialReview.user?.avatar_url ?? uName.slice(0, 1).toUpperCase(),
      avatarColor: "#0f3a4a",
      stars: initialReview.stars ?? 5,
      title: initialReview.title ?? "",
      text: initialReview.content ?? "",
      time: initialReview.created_at ? new Date(initialReview.created_at).toLocaleDateString("vi-VN") : "",
      likes: initialReview.like_count ?? 0,
      liked: false,
      org: typeof initialReview.org === "string"
      ? initialReview.org
      : (initialReview.org?.name ?? ""),
    position: typeof initialReview.position === "string"
      ? initialReview.position
      : (initialReview.position?.name ?? ""),
      replies: [],
    } as ReviewData;
  });
  const [loadingReview, setLoadingReview] = useState(!!reviewId && !initialReview);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState<number>(initialReview?.like_count ?? 0);
  const [showReplies, setShowReplies] = useState(true);
  const [attachedQuestions, setAttachedQuestions] = useState<AttachedQuestion[]>(
    (initialReview?.attached_questions ?? []).map((q: any) => ({
      id: q.id ?? "",
      text: q.content ?? q.text ?? "",
      difficulty: q.difficulty ?? "Trung bình",
      section: "",
      type: (q.type === "new" ? "new" : "matched") as "new" | "matched",
      matchRate: q.matchRate,
    }))
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editText, setEditText] = useState("");
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  const sortedRelated = useMemo(() => [] as RelatedItem[], []);
  const matchedCount = attachedQuestions.filter((q) => q.type === "matched").length;
  const newCount = attachedQuestions.filter((q) => q.type === "new").length;

  // ── Fetch review from DB ──
  useEffect(() => {
    if (!reviewId) return;
    if (initialReview) return;

    let cancelled = false;

    (async () => {
      try {
        const { createClient } = await import("@/utils/supabase/client");
        const supabase = createClient();

        const { data } = await supabase
          .from("reviews")
          .select(`id, title, content, stars, created_at, tags, user_id, attached_questions,
           like_count, comment_count, view_count, total_donations, org, position`)
          .eq("id", reviewId)
          .single();

        if (cancelled) return;

        if (data) {
          let name = "Người dùng";
          let uId = data.user_id ?? "";
          let avatarUrl = "";
          if (data.user_id) {
            const { data: u } = await supabase
              .from("users")
              .select("id, full_name, username, avatar_url")
              .eq("id", data.user_id)
              .single();
            if (u) {
              name = u.full_name ?? u.username ?? "Người dùng";
              uId = u.id;
              avatarUrl = u.avatar_url ?? "";
            }
          }

          if (cancelled) return;

          setDbReview({
            id: data.id,
            author: name,
            authorId: uId,
            avatar: avatarUrl || name.slice(0, 2).toUpperCase(),
            avatarColor: "#0f3a4a",
            stars: data.stars ?? 5,
            title: data.title ?? "",
            text: data.content ?? "",
            time: data.created_at ? new Date(data.created_at).toLocaleDateString("vi-VN") : "",
            likes: data.like_count ?? 0,
            liked: false,
            org: data.org ?? "",
            position: data.position ?? "",
            replies: [],
          });
          setLikeCount(data.like_count ?? 0);

          // Check owner
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (cancelled) return;
          if (authUser?.id && authUser.id === data.user_id) setIsOwner(true);

          if (data.attached_questions?.length) {
            setAttachedQuestions(data.attached_questions.map((q: any) => ({
              id: q.id ?? "",
              text: q.content ?? q.text ?? "",
              difficulty: q.difficulty ?? "Trung bình",
              section: "",
              type: (q.type === "new" ? "new" : "matched") as "new" | "matched",
              matchRate: q.matchRate,
            })));
          }

          // Tăng view count (fire-and-forget, không await)
          supabase
            .from("reviews")
            .update({ view_count: (data.view_count ?? 0) + 1 })
            .eq("id", reviewId)
            .then(() => {});
        }
      } catch (err) {
        console.error("[ReviewDetailPage] Fetch failed:", err);
      } finally {
        if (!cancelled) setLoadingReview(false);
      }
    })();

    return () => { cancelled = true; };
  }, [reviewId, initialReview]);

  // ── Handlers ──
  const toggleLike = async () => {
    const newLiked = !liked;
    setLiked(newLiked);
    setLikeCount((p) => newLiked ? p + 1 : p - 1);
    try {
      const supabase = (await import("@/utils/supabase/client")).createClient();
      await supabase.from("reviews").update({ like_count: newLiked ? likeCount + 1 : likeCount - 1 }).eq("id", reviewId);
    } catch {}
  };

  const handleSaveEdit = () => { setIsEditing(false); };

  const handleCancelEdit = () => {
    setEditTitle(dbReview?.title ?? "");
    setEditText(dbReview?.text ?? "");
    setIsEditing(false);
  };

  const addMatchedQuestion = (q: typeof GLOBAL_QUESTION_BANK[0]) => {
    if (attachedQuestions.find((aq) => aq.id === q.id)) return;
    setAttachedQuestions((prev) => [...prev, { ...q, type: "matched", matchRate: `${60 + Math.floor(Math.random() * 35)}%` }]);
  };

  const addNewQuestion = (text: string, difficulty: string) => {
    const id = `nq-${Date.now()}`;
    setAttachedQuestions((prev) => [...prev, { id, text, difficulty, section: "Người dùng tạo", type: "new" }]);
  };

  const removeAttached = (id: string) => {
    setAttachedQuestions((prev) => prev.filter((q) => q.id !== id));
  };

  // ── Early returns AFTER all hooks ──
  if (loadingReview) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!dbReview) {
    return (
      <div className="text-center py-20">
        <p className="text-[14px] font-semibold text-[#94a3b8]">Không tìm thấy bài đánh giá</p>
        <button onClick={onBack} className="mt-3 text-[12px] text-teal-600 font-bold hover:underline">← Quay lại</button>
      </div>
    );
  }

  const review = dbReview;
  const isAuthor = isOwner;

  return (
    <div className="max-w-[72rem] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pt-2 pb-6 md:pb-8">

      {/* ── Action bar: Copy UUID + More menu ── */}
      <div className="flex items-center justify-end gap-2 mb-3">
        <button
          onClick={handleCopyUUID}
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-border bg-white text-[12px] font-semibold text-foreground hover:bg-muted transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-emerald-700">Đã copy</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5 text-teal-600" />
              <span>Copy UUID</span>
            </>
          )}
        </button>

        <div className="relative shrink-0">
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Thêm"
          >
            <MoreVertical className="w-4 h-4" />
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-10 z-50 w-48 bg-white border border-border rounded-xl shadow-lg overflow-hidden">
                <button
                  onClick={() => { setMenuOpen(false); /* TODO: report */ }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-foreground hover:bg-muted/60 transition-colors"
                >
                  <Flag className="w-4 h-4 text-red-500" />
                  <span className="font-semibold">Báo cáo</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ══ TWO-COLUMN ══ */}
      <div className="flex gap-6 items-start max-lg:flex-col">

        {/* ════ LEFT: Main Content (70%) ════ */}
        <div className="flex-1 min-w-0">
          <div className="bg-white border border-border rounded-xl shadow-card p-3 sm:p-5 mb-5 overflow-x-hidden">
            {/* Header: Avatar + Meta */}
<div className="flex items-center gap-3 mb-4">
  {review.avatar?.startsWith("http") ? (
    <img src={review.avatar} alt={review.author} className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover shrink-0 border border-[#e2e8f0]" />
  ) : (
    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white text-sm font-extrabold shrink-0" style={{ background: review.avatarColor }}>
      {review.avatar}
    </div>
  )}
  <div className="flex-1 min-w-0">
    <div className="flex items-center gap-2 flex-wrap">
      {review.authorId ? (
        <UserHoverCard
          userId={review.authorId}
          name={review.author}
          onNavigate={onNavigate}
          className="text-[14px] font-semibold text-teal-800 hover:text-orange-500 hover:underline cursor-pointer transition-colors"
        />
      ) : (
        <span className="text-[14px] font-semibold text-foreground">{review.author}</span>
      )}
      <span className="text-[11px] text-muted-foreground">{review.time}</span>
    </div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star key={j} className={`w-3.5 h-3.5 ${j < review.stars ? "fill-amber-400 text-amber-400" : "text-muted-foreground/20"}`} />
                    ))}
                  </div>
                  {review.org && <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[9px] font-bold bg-teal-50 text-teal-700 border border-teal-200">{review.org}</span>}
                  <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <CheckCircle2 className="w-3 h-3" />Đã trúng tuyển
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 mr-1 sm:mr-2">
                {isAuthor && (
                  <Link href={`/studio/reviews/${review.id}/edit`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-border text-[11px] font-semibold text-muted-foreground hover:bg-muted transition-all">
                    <Pencil className="w-3 h-3" />Sửa
                  </Link>
                )}
                <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-500 text-white text-[11px] font-bold hover:bg-orange-600 transition-colors shadow-[0_2px_8px_rgba(249,115,22,.3)]">
                  <Gift className="w-3.5 h-3.5" />Donate
                </button>
              </div>
            </div>

            {/* Title — full width, no indent */}
            {isEditing ? (
              <EditForm title={editTitle} text={editText} onTitleChange={setEditTitle} onTextChange={setEditText} onSave={handleSaveEdit} onCancel={handleCancelEdit} />
            ) : (
              <>
                {review.title && <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground mb-3 break-words" style={{ overflowWrap: "anywhere" }}>{review.title}</h2>}
                <div
                  className="prose prose-sm md:prose-base max-w-none w-full break-words text-foreground/85 leading-[1.75]
                    [overflow-wrap:anywhere] [word-break:break-word]
                    [&_img]:max-w-full [&_img]:w-full [&_img]:h-auto [&_img]:rounded-xl [&_img]:mx-auto [&_img]:my-3
                    [&_a]:break-all [&_a]:text-teal-600 [&_a]:underline
                    [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto
                    [&_strong]:font-bold [&_em]:italic [&_u]:underline
                    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1"
                  dangerouslySetInnerHTML={{ __html: review.text }}
                />
              </>
            )}

                {/* Attached Questions */}
                {attachedQuestions.length > 0 && !isEditing && (
                  <div id="attached-questions" className="mb-4 pt-3 border-t border-border">
                    <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <FileText className="w-3 h-3" />Câu hỏi đính kèm ({attachedQuestions.length})
                      {matchedCount > 0 && <span className="text-teal-600 normal-case">· 🔗 Trùng: {matchedCount}</span>}
                      {newCount > 0 && <span className="text-orange-500 normal-case">· ✨ Mới: {newCount}</span>}
                    </div>
                    {/* Nhóm 1: Câu Mới */}
                    {newCount > 0 && (
                      <div className="mb-2 p-2.5 rounded-xl bg-emerald-50/50 border border-emerald-200">
                        <div className="text-[10px] font-bold text-emerald-700 mb-1.5 flex items-center gap-1"><Sparkles className="w-3 h-3" />Câu hỏi mới ({newCount})</div>
                        <div className="flex flex-col gap-1.5">
                          {attachedQuestions.filter((q) => q.type === "new").map((q) => (
                            <div key={q.id} className="flex items-start gap-2.5 px-3 py-2 rounded-lg bg-white border border-emerald-200">
                              <div className="w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5 bg-emerald-50 text-emerald-600">
                                <Sparkles className="w-3 h-3" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[12px] text-foreground font-medium leading-snug break-words" style={{ overflowWrap: "anywhere" }}>{q.text}</p>
                                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                                  <span className={q.difficulty === "Khó" ? "text-red-500" : q.difficulty === "Dễ" ? "text-emerald-600" : "text-amber-600"}>{q.difficulty}</span>
                                  <span className="text-emerald-600 font-semibold">✨ Mới</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Nhóm 2: Câu Trùng */}
                    {matchedCount > 0 && (
                      <div className="p-2.5 rounded-xl bg-slate-50/50 border border-slate-200">
                        <div className="text-[10px] font-bold text-slate-600 mb-1.5 flex items-center gap-1"><Link2 className="w-3 h-3" />Câu hỏi trùng ({matchedCount})</div>
                        <div className="flex flex-col gap-1.5">
                          {attachedQuestions.filter((q) => q.type === "matched").map((q) => (
                            <div key={q.id} className="flex items-start gap-2.5 px-3 py-2 rounded-lg bg-white border border-border">
                              <div className="w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5 bg-teal-50 text-teal-600">
                                <Link2 className="w-3 h-3" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[12px] text-foreground font-medium leading-snug break-words" style={{ overflowWrap: "anywhere" }}>{q.text}</p>
                                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                                  <span>{q.section}</span><span>·</span>
                                  <span className={q.difficulty === "Khó" ? "text-red-500" : q.difficulty === "Dễ" ? "text-emerald-600" : "text-amber-600"}>{q.difficulty}</span>
                                  {q.matchRate && <><span>·</span><span className="text-teal-600 font-semibold">Trùng {q.matchRate}</span></>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action bar */}
            <div className="flex items-center gap-1 pt-2 border-t border-border flex-wrap -mx-0.5">
                  <button onClick={toggleLike} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all ${liked ? "text-orange-500 bg-orange-50" : "text-muted-foreground hover:bg-muted"}`}>
                    <ThumbsUp className={`w-3.5 h-3.5 ${liked ? "fill-orange-500" : ""}`} />{likeCount}
                  </button>
                  <button onClick={() => setShowReplies((p) => !p)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all ${showReplies ? "text-teal-700 bg-teal-50" : "text-muted-foreground hover:bg-muted"}`}>
                    <MessageSquare className="w-3.5 h-3.5" />{review.replies.length}
                  </button>
                  {isAuthor && (
                    <>
                      <button onClick={() => setShowMatchModal(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold text-muted-foreground hover:bg-teal-50 hover:text-teal-700 transition-all">
                        <Link2 className="w-3.5 h-3.5" />Trùng {matchedCount > 0 && `(${matchedCount})`}
                      </button>
                      <button onClick={() => setShowNewModal(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold text-muted-foreground hover:bg-orange-50 hover:text-orange-500 transition-all">
                        <Sparkles className="w-3.5 h-3.5" />Mới {newCount > 0 && `(${newCount})`}
                      </button>
                    </>
                  )}
                  <div className="flex-1" />
                  <button className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-muted-foreground hover:bg-muted transition-all"><Bookmark className="w-3.5 h-3.5" /></button>
                  <button className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-muted-foreground hover:bg-muted transition-all"><Share2 className="w-3.5 h-3.5" /></button>
                </div>

            {/* Replies */}
            {showReplies && (
              <div id="comment-section" className="mt-4 pt-2 border-t border-border">
                <ReviewDetail review={review} onNavigate={onNavigate} />
              </div>
            )}
          </div>
        </div>

        {/* ════ RIGHT: Sidebar (30%) ════ */}
        <div className="w-full lg:w-[280px] xl:w-[300px] shrink-0 lg:sticky lg:top-8">
          <RelatedSidebar items={sortedRelated} matchedCount={matchedCount} onNavigate={onNavigate} />

          {/* Stats */}
          <div className="bg-white border border-border rounded-xl shadow-card p-4 mb-4">
            <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Thống kê</div>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { label: "Lượt xem", value: ((review as any).view_count ?? 0).toLocaleString(), icon: Eye, onClick: undefined as (() => void) | undefined },
                { label: "Lượt thích", value: likeCount.toLocaleString(), icon: ThumbsUp, onClick: toggleLike },
                { label: "Phản hồi", value: ((review as any).comment_count ?? 0).toLocaleString(), icon: MessageSquare, onClick: () => { setShowReplies(true); setTimeout(() => document.getElementById("comment-section")?.scrollIntoView({ behavior: "smooth" }), 100); } },
                { label: "Câu đính kèm", value: String(attachedQuestions.length), icon: FileText, onClick: () => document.getElementById("attached-questions")?.scrollIntoView({ behavior: "smooth" }) },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <button key={s.label} onClick={s.onClick} className={`text-center p-2.5 rounded-lg bg-muted/40 transition-colors ${s.onClick ? "hover:bg-muted cursor-pointer" : ""}`}>
                    <Icon className="w-4 h-4 text-teal-500 mx-auto mb-1" />
                    <div className="font-display text-sm font-extrabold text-teal-800">{s.value}</div>
                    <div className="text-[9px] text-muted-foreground font-medium">{s.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Donate CTA */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-center shadow-[0_4px_16px_rgba(249,115,22,.25)]">
            <Gift className="w-6 h-6 text-white/90 mx-auto mb-2" />
            <div className="text-[13px] font-bold text-white mb-1">Ủng hộ người review</div>
            <p className="text-[11px] text-white/70 leading-relaxed mb-3">Cảm ơn chia sẻ hữu ích!</p>
            <button className="w-full py-2.5 rounded-full bg-white text-orange-600 text-[12px] font-bold hover:bg-orange-50 transition-colors">💎 Donate ngay</button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showMatchModal && (
        <MatchingQuestionsModal bank={[]} alreadyAttached={attachedQuestions.map((q) => q.id)} onAdd={addMatchedQuestion} onClose={() => setShowMatchModal(false)} />
      )}
      {showNewModal && (
        <NewQuestionModal onAdd={addNewQuestion} onClose={() => setShowNewModal(false)} />
      )}

      <div className="h-8" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   EDIT FORM
═══════════════════════════════════════════════════ */

function EditForm({ title, text, onTitleChange, onTextChange, onSave, onCancel }: {
  title: string; text: string;
  onTitleChange: (v: string) => void; onTextChange: (v: string) => void;
  onSave: () => void; onCancel: () => void;
}) {
  return (
    <div className="mt-3 mb-3">
      <input value={title} onChange={(e) => onTitleChange(e.target.value)} placeholder="Tiêu đề bài review..."
        className="w-full px-3 py-2 rounded-xl border border-border bg-white text-[15px] font-bold text-foreground outline-none focus:border-teal-600 focus:shadow-[0_0_0_3px_rgba(30,90,112,.06)] transition-all mb-2 placeholder:text-muted-foreground/50" />
      <textarea value={text} onChange={(e) => onTextChange(e.target.value)} rows={5} placeholder="Nội dung bài review..."
        className="w-full px-3 py-2.5 rounded-xl border border-border bg-white text-[14px] text-foreground leading-relaxed outline-none focus:border-teal-600 focus:shadow-[0_0_0_3px_rgba(30,90,112,.06)] transition-all resize-none placeholder:text-muted-foreground/50" />
      <div className="flex items-center gap-2 mt-2">
        <button onClick={onSave} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-teal-800 text-white text-[12px] font-bold hover:bg-teal-700 transition-colors"><Save className="w-3.5 h-3.5" />Lưu</button>
        <button onClick={onCancel} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-[12px] font-semibold text-muted-foreground hover:bg-muted transition-colors">Hủy</button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   RELATED SIDEBAR
═══════════════════════════════════════════════════ */

function RelatedSidebar({ items, matchedCount, onNavigate }: { items: RelatedItem[]; matchedCount: number; onNavigate?: (page: string) => void }) {
  const paths = items.filter((i) => i.type === "path");
  const exams = items.filter((i) => i.type === "exam");

  return (
    <>
      {paths.length > 0 && (
        <div className="bg-white border border-border rounded-xl shadow-card p-4 mb-4">
          <div className="flex items-center gap-2 mb-3"><BookOpen className="w-4 h-4 text-teal-600" /><span className="text-[13px] font-bold text-teal-800">Lộ trình liên quan</span></div>
          <p className="text-[10px] text-muted-foreground mb-3">Sắp xếp theo số câu hỏi trùng với bài review</p>
          <div className="flex flex-col gap-2">{paths.map((item) => <RelatedCard key={item.id} item={item} onNavigate={onNavigate} />)}</div>
        </div>
      )}
      {exams.length > 0 && (
        <div className="bg-white border border-border rounded-xl shadow-card p-4 mb-4">
          <div className="flex items-center gap-2 mb-3"><FileText className="w-4 h-4 text-orange-500" /><span className="text-[13px] font-bold text-teal-800">Đề thi liên quan</span></div>
          <p className="text-[10px] text-muted-foreground mb-3">Đề thi chứa câu hỏi trùng nhiều nhất</p>
          <div className="flex flex-col gap-2">{exams.map((item) => <RelatedCard key={item.id} item={item} onNavigate={onNavigate} />)}</div>
        </div>
      )}
    </>
  );
}

function RelatedCard({ item, onNavigate }: { item: RelatedItem; onNavigate?: (page: string) => void }) {
  return (
    <div onClick={() => onNavigate?.(item.type === "path" ? "path-detail" : "list-exams")}
      className="flex gap-3 items-start p-2.5 rounded-lg border border-border hover:border-teal-500/40 hover:shadow-card-md hover:-translate-y-px transition-all cursor-pointer">
      <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-lg shrink-0">{item.emoji}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-bold text-teal-800 leading-snug truncate mb-0.5">{item.title}</div>
        <div className="text-[10px] text-muted-foreground mb-1">by {item.author}</div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="text-amber-500 font-semibold">★ {item.rating}</span>
          <span className="text-muted-foreground">{item.totalQuestions} câu</span>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[9px] font-bold bg-teal-50 text-teal-700 border border-teal-200">
          <Target className="w-2.5 h-2.5" />Trùng {item.matchCount}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MATCHING QUESTIONS MODAL
═══════════════════════════════════════════════════ */

function MatchingQuestionsModal({ bank, alreadyAttached, onAdd, onClose }: {
  bank: typeof GLOBAL_QUESTION_BANK; alreadyAttached: string[];
  onAdd: (q: typeof GLOBAL_QUESTION_BANK[0]) => void; onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const filtered = bank.filter((q) => q.text.toLowerCase().includes(search.toLowerCase()) || q.section.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden animate-fade-slide max-h-[80vh] flex flex-col">
        <div className="px-5 pt-5 pb-3 border-b border-border shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-[16px] font-extrabold text-teal-800 flex items-center gap-2"><Link2 className="w-5 h-5 text-teal-600" />Câu hỏi trùng</h3>
            <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-[12px] text-muted-foreground mb-3">Tìm và chọn câu hỏi bạn đã gặp khi thi thực tế</p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm câu hỏi..."
              className="w-full h-9 rounded-xl border border-border bg-white pl-9 pr-4 text-[12px] outline-none focus:border-teal-600 transition-all placeholder:text-muted-foreground" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
          {filtered.length === 0 ? (
            <p className="text-center text-[12px] text-muted-foreground py-6">Không tìm thấy câu hỏi phù hợp</p>
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map((q) => {
                const isAdded = alreadyAttached.includes(q.id);
                return (
                  <div key={q.id} className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${isAdded ? "border-teal-400 bg-teal-50/50" : "border-border hover:border-teal-500/40"}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-medium text-foreground leading-snug mb-1">{q.text}</p>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span>{q.section}</span><span>·</span>
                        <span className={q.difficulty === "Khó" ? "text-red-500" : q.difficulty === "Dễ" ? "text-emerald-600" : "text-amber-600"}>{q.difficulty}</span>
                      </div>
                    </div>
                    <button onClick={() => !isAdded && onAdd(q)} disabled={isAdded}
                      className={`shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${isAdded ? "bg-teal-100 text-teal-700 cursor-default" : "bg-teal-800 text-white hover:bg-teal-700"}`}>
                      {isAdded ? <><CheckCircle2 className="w-3 h-3 inline mr-1" />Đã thêm</> : <><Plus className="w-3 h-3 inline mr-1" />Chọn</>}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   NEW QUESTION MODAL
═══════════════════════════════════════════════════ */

function NewQuestionModal({ onAdd, onClose }: { onAdd: (text: string, difficulty: string) => void; onClose: () => void }) {
  const [tab, setTab] = useState<"ai" | "manual">("manual");
  const [text, setText] = useState("");
  const [difficulty, setDifficulty] = useState("Trung bình");
  const [aiPrompt, setAiPrompt] = useState("");

  const handleSubmit = () => { if (!text.trim()) return; onAdd(text.trim(), difficulty); setText(""); setDifficulty("Trung bình"); };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden animate-fade-slide">
        <div className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-[16px] font-extrabold text-teal-800 flex items-center gap-2"><Sparkles className="w-5 h-5 text-orange-500" />Câu hỏi mới</h3>
            <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-1 p-1 bg-muted rounded-xl">
            <button onClick={() => setTab("manual")} className={`flex-1 py-2 rounded-lg text-[12px] font-semibold transition-all ${tab === "manual" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground"}`}><Pencil className="w-3 h-3 inline mr-1" />Nhập thủ công</button>
            <button onClick={() => setTab("ai")} className={`flex-1 py-2 rounded-lg text-[12px] font-semibold transition-all ${tab === "ai" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground"}`}><Bot className="w-3 h-3 inline mr-1" />Tạo bằng AI</button>
          </div>
        </div>
        <div className="p-5">
          {tab === "manual" ? (
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-[12px] font-semibold text-foreground mb-1.5 block">Nội dung câu hỏi</label>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Nhập nội dung câu hỏi bạn gặp khi thi..."
                  className="w-full px-3 py-2.5 rounded-xl border border-border text-[13px] outline-none focus:border-teal-600 transition-all resize-none placeholder:text-muted-foreground/50" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-foreground mb-1.5 block">Mức độ</label>
                <div className="flex gap-2">
                  {["Dễ", "Trung bình", "Khó"].map((d) => (
                    <button key={d} onClick={() => setDifficulty(d)} className={`flex-1 py-2 rounded-xl text-[12px] font-bold border transition-all ${difficulty === d ? "bg-teal-800 text-white border-teal-800" : "border-border text-foreground hover:border-teal-500"}`}>{d}</button>
                  ))}
                </div>
              </div>
              <button onClick={handleSubmit} disabled={!text.trim()} className={`w-full py-2.5 rounded-full text-[13px] font-bold transition-all ${text.trim() ? "bg-orange-500 text-white hover:bg-orange-600 shadow-[0_2px_10px_rgba(249,115,22,.3)]" : "bg-muted text-muted-foreground cursor-not-allowed"}`}>
                <Plus className="w-4 h-4 inline mr-1" />Thêm câu hỏi
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-[12px] font-semibold text-foreground mb-1.5 block">Mô tả cho AI</label>
                <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={3}
                  placeholder="VD: Tạo câu hỏi trắc nghiệm về quy trình cho vay KHCN tại Vietcombank..."
                  className="w-full px-3 py-2.5 rounded-xl border border-border text-[13px] outline-none focus:border-teal-600 transition-all resize-none placeholder:text-muted-foreground/50" />
              </div>
              <button disabled={!aiPrompt.trim()} className={`w-full py-2.5 rounded-full text-[13px] font-bold transition-all flex items-center justify-center gap-1.5 ${aiPrompt.trim() ? "bg-[#7c3aed] text-white hover:bg-[#6d28d9]" : "bg-muted text-muted-foreground cursor-not-allowed"}`}>
                <Bot className="w-4 h-4" />Tạo bằng AI
              </button>
              <div className="bg-[#f5f3ff] rounded-xl p-3 text-[11px] text-[#7c3aed]/80 border border-[#ede9fe]">
                🤖 AI sẽ phân tích mô tả và tạo câu hỏi trắc nghiệm chuẩn format.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}